import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Panel } from '@tanium/react-panel';
import { RibbonColumn, StackedRibbonList } from '@tanium/react-stacked-ribbon-list';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Stack } from '@tanium/react-stack';
import { Link } from '@tanium/react-link';
import Request from 'gql/Request';
import RequestListSlice from 'components/pages/console/store/RequestListSlice';
import RequestListSelectors from 'components/pages/console/store/RequestListSelectors';
import Helper from 'utils/Helper';
import { CHANGE_REQUEST_ROUTE, REQUEST_ROUTE } from 'Route';
import { ActionStatus } from 'store/ActionState';
import moment, { Moment } from 'moment';
import { Button } from '@tanium/react-button';
import { PagerStatus } from 'store/PagerState';
import DateFilter from 'components/pages/console/consolePartials/requestListpartials/DateFilter';
import { CHANGE_REQUEST_PREFIX } from 'components/pages/changeRequest/ChangeRequestPage';
import Tooltip from 'components/tooltip/Tooltip';
import { Box } from '@tanium/react-box';
import reactStringReplace from 'react-string-replace';
import MediumBoldText from 'components/styles/text/MediumBoldText';

export interface IRequestListPanelProps {
  targetId: string;
}

const usePrevious = <T,>(value: T): T | undefined => {
  const ref = useRef<T>();
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
};

export const RequestListPanel = ({ targetId }: IRequestListPanelProps): React.ReactElement => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const requests = useSelector(RequestListSelectors.selectRequests);
  const listRequestsState = useSelector(RequestListSelectors.selectListRequestsState);
  const listRequestsPagerState = useSelector(RequestListSelectors.selectPagerState);
  const dataGetter = useCallback((index: number) => requests[index], [requests]);
  const itemIdGetter = useCallback((index) => dataGetter(index).id, [dataGetter]);

  const [startDate, setStartDate] = useState<Moment>(moment().subtract(1, 'days'));
  const previousStartDate = usePrevious(startDate);
  const [endDate, setEndDate] = useState<Moment>(moment());
  const previousEndDate = usePrevious(endDate);

  const dispatchListRequests = useCallback(() => {
    dispatch(
      RequestListSlice.actions.listRequests({
        targetId,
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        nextToken: listRequestsPagerState.nextToken,
      })
    );
  }, [dispatch, startDate, endDate, listRequestsPagerState.nextToken, targetId]);

  useEffect(() => {
    /* istanbul ignore next */
    if (previousStartDate !== startDate || previousEndDate !== endDate) {
      dispatch(RequestListSlice.actions.clearState());
    }
    /* istanbul ignore next */
    if (moment(startDate).isAfter(endDate)) {
      /* istanbul ignore next */
      setEndDate(startDate);
    }

    dispatchListRequests();

    return () => {
      dispatch(RequestListSlice.actions.clearState());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, targetId, startDate, endDate]);

  const dateFilters = (
    <DateFilter
      startDate={startDate}
      startMin={moment().subtract(18, 'months')}
      endDate={endDate}
      endMax={moment()}
      onStartDateChange={setStartDate}
      onEndDateChange={setEndDate}
    />
  );

  const requestPager = (
    <div style={{ marginLeft: '0.5em' }}>
      <Button
        data-testid="load-more-requests-button"
        variant="primary"
        onClick={dispatchListRequests}
        disabled={listRequestsPagerState.status !== PagerStatus.DISPLAYED}
      >
        {t('LOAD_MORE_ITEMS')}
      </Button>
    </div>
  );

  const listActions = (
    <Stack>
      {dateFilters}
      {requestPager}
    </Stack>
  );

  const columns: RibbonColumn<Request>[] = useMemo(
    () => [
      {
        label: t('ID'),
        dataKey: 'requestId',
        primary: true,
        renderer: ({ itemData }) => {
          const requestRoute = itemData.id.startsWith(CHANGE_REQUEST_PREFIX) ? CHANGE_REQUEST_ROUTE : REQUEST_ROUTE;
          return (
            <Link href={Helper.interpolateRoute(requestRoute.url, { requestId: itemData.id })}>
              <div className="truncated-text">{Helper.humanReadableId(itemData.id)}</div>
            </Link>
          );
        },
      },
      {
        label: t('CREATED_BY'),
        dataKey: 'createdBy',
        renderer: ({ itemData }) => {
          const emailObject = Helper.emailSplit(itemData.createdBy);
          return (
            <Tooltip content={itemData.createdBy}>
              <Box display={'flex'}>{emailObject.username}</Box>
            </Tooltip>
          );
        },
      },
      {
        label: t('CREATED_AT'),
        dataKey: 'createdAt',
        renderer: ({ itemData }) => {
          return <div className="truncated-text">{Helper.localDateIn12HrFormat(itemData.createdAt)}</div>;
        },
      },
      {
        label: t('ACCESS_TYPES'),
        dataKey: 'accessType',
        renderer: ({ itemData }) => {
          return (
            <div className="truncated-text">
              {itemData.access?.map((access, i) => (
                <div key={`truncated-text-${i}`}>
                  <MediumBoldText>{t(access.type)}</MediumBoldText>
                </div>
              ))}
            </div>
          );
        },
      },
      {
        label: t('JUSTIFICATION'),
        dataKey: 'details',
        renderer: ({ itemData }) => {
          return (
            <div className="truncated-text">
              {reactStringReplace(itemData.details, Helper.URL_REGEX, (url: string) => (
                <Tooltip content={itemData.details}>
                  <Link target={'_blank'} rel={'noreferrer'} href={Helper.externalUrl(url)}>
                    {url}
                  </Link>
                </Tooltip>
              ))}
            </div>
          );
        },
      },
      {
        label: t('TICKET_INFO'),
        dataKey: 'ticketInfo',
        renderer: ({ itemData }) => {
          return (
            <div className="truncated-text">
              {reactStringReplace(itemData.ticketInfo, Helper.URL_REGEX, (url: string) => (
                <Tooltip content={itemData.ticketInfo}>
                  <Link target={'_blank'} rel={'noreferrer'} href={Helper.externalUrl(url)}>
                    {url}
                  </Link>
                </Tooltip>
              ))}
            </div>
          );
        },
      },
    ],
    [t]
  );

  const isItemLoaded = useCallback(
    (index: number) => {
      return !!dataGetter(index);
    },
    [dataGetter]
  );

  const isIdEmphasized = useCallback(
    (id: string) => {
      const item = requests.find((role) => role.id === id);
      return item?.targetId === targetId;
    },
    [requests, targetId]
  );

  return (
    <div className="request-list-panel">
      <Panel title={t('REQUEST_LIST_PANEL_TITLE')}>
        <Stack direction="column" itemSpacing={1} data-testid="requestListPanel">
          <StackedRibbonList
            enableRowHighlight={false}
            columns={columns}
            isItemLoaded={isItemLoaded}
            height={requests.length > 0 ? '12em' : '7.4em'}
            dataGetter={dataGetter}
            itemCount={requests.length}
            itemIdGetter={itemIdGetter}
            isLoading={listRequestsState === ActionStatus.PROCESSING}
            isIdEmphasized={isIdEmphasized}
            listActions={listActions}
          />
        </Stack>
      </Panel>
    </div>
  );
};

export default RequestListPanel;
